/**
 * "Sign in with LinkedIn" for the web app: Supabase's LinkedIn (OIDC) provider.
 *
 * Unlike Google (lib/googleSignIn.ts), nothing here needs a Howie-specific client: LinkedIn's consent
 * screen shows the LinkedIn developer app, which is Howie's own, so Supabase's redirect flow is used
 * as-is. begin() remembers where to continue afterwards and sends the browser to Supabase, which sends
 * it to LinkedIn and back to /auth/linkedin/callback with the session in the URL fragment (the Supabase
 * client picks that up by itself). complete() waits for that session, checks that LinkedIn vouched for an
 * email (otherwise Howie cannot match an account), and returns where to go next.
 */

export const LINKEDIN_CALLBACK_PATH = '/auth/linkedin/callback';
const STORAGE_KEY = 'howie.linkedinSignIn';
const DEFAULT_SESSION_WAIT = { attempts: 40, delayMs: 100 };

export const LINKEDIN_CANCELLED_MESSAGE = 'LinkedIn sign-in was cancelled.';
export const LINKEDIN_NO_EMAIL_MESSAGE = 'LinkedIn did not share a verified email with Howie, so Howie cannot match it to an account. Sign in with Google or your email instead.';
export const LINKEDIN_EXPIRED_MESSAGE = 'This sign-in link has expired. Please try again.';

interface PendingSignIn {
  postAuthPath: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface OAuthSignIn {
  (credentials: { provider: 'linkedin_oidc'; options: { redirectTo: string } }): Promise<{ error: { message: string } | null }>;
}

/** Remember where to continue, then hand the browser to Supabase (which redirects to LinkedIn). */
export async function beginLinkedInSignIn(options: {
  origin: string;
  postAuthPath: string;
  signInWithOAuth: OAuthSignIn;
  storage?: StorageLike;
}): Promise<void> {
  const storage = options.storage ?? window.sessionStorage;
  const pending: PendingSignIn = { postAuthPath: options.postAuthPath };
  storage.setItem(STORAGE_KEY, JSON.stringify(pending));
  const { error } = await options.signInWithOAuth({
    provider: 'linkedin_oidc',
    options: { redirectTo: `${options.origin}${LINKEDIN_CALLBACK_PATH}` },
  });
  if (error) {
    storage.removeItem(STORAGE_KEY);
    throw new Error(error.message || 'LinkedIn sign-in could not start.');
  }
}

export interface LinkedInCallback {
  error?: string;
  errorCode?: string;
  errorDescription?: string;
}

/** Supabase reports a refused or failed sign-in in the query or the fragment: `error`, `error_code`, `error_description`. */
export function parseLinkedInCallback(href: string): LinkedInCallback {
  const url = new URL(href);
  const query = url.searchParams;
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  const read = (key: string) => query.get(key) || hash.get(key) || undefined;
  return { error: read('error'), errorCode: read('error_code'), errorDescription: read('error_description') };
}

/** Only the codes a refusal carries; never free text, which could say "cancel" for another reason. */
export function isLinkedInCancelled(callback: LinkedInCallback): boolean {
  if (callback.error === 'access_denied') return true;
  return /^(access_denied|user_cancelled(_login|_authorize)?|user_denied)$/i.test((callback.errorCode ?? '').trim())
    || /^user_cancelled(_login|_authorize)?$/i.test((callback.errorDescription ?? '').trim());
}

/** A remembered return path must be a same-origin path: one leading slash, no scheme, no `//host`. */
export function safePostAuthPath(value: unknown, fallback = '/chat'): string {
  if (typeof value !== 'string') return fallback;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//') || /^\/\\/.test(path) || /[\u0000-\u001f]/.test(path)) return fallback;
  return path;
}

export interface SessionUser {
  email?: string | null;
  email_confirmed_at?: string | null;
  confirmed_at?: string | null;
}

export interface SessionReader {
  (): Promise<{ data: { session: { user: SessionUser } | null } }>;
}

/**
 * Finish the sign-in on the callback page. Returns the path to continue to.
 * Throws with a message fit for the user when LinkedIn refused, when no session arrived, or when the
 * session has no verified email (that session is signed out again first).
 */
export async function completeLinkedInSignIn(options: {
  href: string;
  getSession: SessionReader;
  signOut: () => Promise<unknown>;
  storage?: StorageLike;
  wait?: { attempts: number; delayMs: number };
  sleep?: (ms: number) => Promise<void>;
}): Promise<string> {
  const storage = options.storage ?? window.sessionStorage;
  // Read now, forget only once the outcome is known: React StrictMode runs the callback effect twice in
  // development, and the second run must still find the return path.
  const raw = storage.getItem(STORAGE_KEY);
  const forgetPending = () => storage.removeItem(STORAGE_KEY);
  let postAuthPath = '/chat';
  if (raw) {
    try {
      postAuthPath = safePostAuthPath((JSON.parse(raw) as PendingSignIn).postAuthPath);
    } catch {
      // A stale or hand-edited entry: continue to the default.
    }
  }

  const callback = parseLinkedInCallback(options.href);
  if (callback.error || callback.errorCode || callback.errorDescription) {
    forgetPending();
    if (isLinkedInCancelled(callback)) throw new Error(LINKEDIN_CANCELLED_MESSAGE);
    // The description is provider text from the URL; log it for us, show fixed words to the person.
    console.warn('[Howie] LinkedIn sign-in failed:', callback.error, callback.errorCode, callback.errorDescription);
    throw new Error('LinkedIn could not sign you in. Please try again.');
  }

  // The Supabase client reads the session out of the URL fragment on its own; give it a moment.
  const wait = options.wait ?? DEFAULT_SESSION_WAIT;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let user: SessionUser | undefined;
  for (let attempt = 0; attempt < wait.attempts; attempt += 1) {
    const { data } = await options.getSession();
    if (data.session) {
      user = data.session.user;
      break;
    }
    await sleep(wait.delayMs);
  }
  if (!user) {
    forgetPending();
    throw new Error(LINKEDIN_EXPIRED_MESSAGE);
  }
  forgetPending();
  if (!user.email || !(user.email_confirmed_at || user.confirmed_at)) {
    // The message must reach the person even when the sign-out call itself fails.
    try {
      await options.signOut();
    } catch (error) {
      console.warn('[Howie] could not sign the unverified LinkedIn session out:', error);
    }
    throw new Error(LINKEDIN_NO_EMAIL_MESSAGE);
  }
  return postAuthPath;
}
